import type { MulticolorValue } from '@elfsight/configurator-contracts';
import { isTransparentColor } from '@elfsight/lib-utils';
import type { SourceMetaValue } from '../../../constants';
import type { ColorType } from '../../../types';
import { useLayoutGap } from '../../layout';
import { useSocialFeedSettings } from '../../utils';
import { usePost } from '../Post';

const getNativeColor = (style: ColorType, colors: [string, string]) => {
  if (style === 'native') {
    return colors[0];
  }

  return colors[1] === 'transparent' ? '' : colors[1];
};

const getSourceStyles = (
  currentSource: SourceMetaValue,
  sourceColor: MulticolorValue
) => {
  if (sourceColor.type === 'native') {
    return {
      color: currentSource.color,
      icon: currentSource.nativeIcon
    };
  }

  return {
    color: sourceColor.color,
    icon: currentSource.icon
  };
};

export function usePostSettings() {
  const { gap } = useLayoutGap();
  const {
    isInPopup,
    layout,
    hasMedia,
    hasRepost,
    showMedia,
    hasTextLink,
    source,
    isFirstRow
  } = usePost();
  const {
    postTextColor,
    postLinksColor,
    postStyle,
    postBackgroundColor,
    postOverlayColorCard2,
    postOverlayColorCard3,
    postOverlayTextColor,
    postOverlayLinksColor,
    postShowAuthorName,
    postShowVerifiedBadge,
    postShowAuthorPicture,
    postShowDate,
    postShowActions,
    postShowText,
    postShowSource,
    postShadow,
    postShowShadow,
    postSourceColor,
    postSourceDisplay,
    postImageRatio,
    postBorderRadius,
    postTextStyle,
    postHeightNormalization,
    postTextLength = 'short',
    postVerifiedBadgeColor,

    popupTextColor,
    popupBackgroundColor,
    popupMediaBackgroundColor,
    popupDisplayElements,
    popupSourceColor,
    popupLinksColor,
    popupBorderRadius,
    popupTextStyle,
    popupFollowButtonColor,
    displayHeader,
    headerBackgroundColor
  } = useSocialFeedSettings();

  const popupFontSize = popupTextStyle?.fontSize ?? 16;
  const textFontSize = postTextStyle?.fontSize ?? 14;

  const isOverlayCard = ['card2', 'card3'].includes(layout);
  const nativeColor = source.gradient || source.color;
  const nativeOverlayColor =
    layout === 'card3' ? source.color : source.overlayColor;

  const postOverlayColor =
    layout === 'card2' ? postOverlayColorCard2 : postOverlayColorCard3;

  if (isInPopup) {
    return {
      backgroundColor: popupBackgroundColor,
      popupMediaBackgroundColor,
      textColor: popupTextColor,
      linksColor: popupLinksColor,
      followButtonColor: popupFollowButtonColor,
      textLength: 'full' as const,
      textStyle: {
        ...popupTextStyle,
        fontSize: popupFontSize
      },

      heightNormalization: false,

      showActions: popupDisplayElements.includes('actions'),
      showText: popupDisplayElements.includes('text'),
      showFollowButton: popupDisplayElements.includes('followButton'),

      borderRadius: popupBorderRadius,

      source: {
        show: popupDisplayElements.includes('sourceLink'),
        display: postSourceDisplay,
        title: source.title,
        ...getSourceStyles(source, popupSourceColor)
      },

      user: {
        showName: popupDisplayElements.includes('authorName'),
        showBadge: postShowVerifiedBadge,
        showPicture: popupDisplayElements.includes('authorPicture'),
        showDate: popupDisplayElements.includes('date'),
        show:
          popupDisplayElements.includes('authorName') ||
          popupDisplayElements.includes('authorPicture') ||
          popupDisplayElements.includes('date')
      }
    };
  }

  return {
    postVerifiedBadgeColor: {
      ...postVerifiedBadgeColor,
      type: postStyle
    },
    backgroundColor:
      isOverlayCard && !showMedia
        ? getNativeColor(postStyle, [nativeColor, postOverlayColor])
        : getNativeColor(postStyle, [nativeColor, postBackgroundColor]),
    textColor: getNativeColor(postStyle, [
      '#fff',
      isOverlayCard ? postOverlayTextColor : postTextColor
    ]),
    linksColor: getNativeColor(postStyle, [
      '#fff',
      isOverlayCard ? postOverlayLinksColor : postLinksColor
    ]),
    overlayColor:
      postStyle === 'native' ? nativeOverlayColor : postOverlayColor,
    textLength: postTextLength,
    textStyle: {
      ...postTextStyle,
      fontSize:
        hasMedia || hasTextLink || hasRepost ? textFontSize : textFontSize + 4
    },

    heightNormalization: postHeightNormalization,

    showActions: postShowActions,

    postImageRatio,

    showText: postShowText,

    borderRadius:
      !gap &&
      isFirstRow &&
      displayHeader &&
      !isTransparentColor(headerBackgroundColor)
        ? `0 0 ${postBorderRadius}px ${postBorderRadius}px`
        : postBorderRadius,
    borderRadiusPreviewLink:
      postBackgroundColor === 'transparent' && !isOverlayCard
        ? postBorderRadius
        : 4,

    showShadow: postShowShadow,
    shadow: postShadow,

    source: {
      show: postShowSource,
      display: postSourceDisplay,
      title: source.title,
      ...(postStyle === 'native' || isOverlayCard
        ? getSourceStyles(source, { type: 'custom', color: '#fff' })
        : getSourceStyles(source, postSourceColor))
    },

    user: {
      showName: postShowAuthorName,
      showBadge: postShowVerifiedBadge,
      showPicture: postShowAuthorPicture,
      showDate: postShowDate,
      show: postShowAuthorName || postShowAuthorPicture || postShowDate
    }
  };
}
